import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Validation schema for signup
const signupSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters')
    .regex(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces'),
  
  email: z.string()
    .email('Invalid email address'),
  
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      'Password must include uppercase, lowercase, number, and special character'
    ),
  
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword']
});

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    setError 
  } = useForm({
    resolver: zodResolver(signupSchema)
  });

  const onSubmit = async (data) => {
    try {
      // Extract only required fields for signup
      const { name, email, password } = data;
      
      // Attempt signup
      await signup(name, email, password);
      
      // Redirect to dashboard on successful signup
      navigate('/dashboard');
    } catch (error) {
      // Handle signup errors
      setError('root', {
        message: error.message || 'Signup failed. Please try again.'
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create Your Knivok Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join Knivok Innovators and start sharing your ideas
          </p>
        </div>
        <form 
          className="mt-8 space-y-6" 
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="rounded-md shadow-sm space-y-4">
            {/* Name Input */}
            <div>
              <label htmlFor="name" className="sr-only">Full Name</label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className={`appearance-none relative block w-full px-3 py-2 border 
                  ${errors.name ? 'border-red-500' : 'border-gray-300'}
                  placeholder-gray-500 text-gray-900 rounded-md focus:outline-none 
                  focus:ring-brand-500 focus:border-brand-500 focus:z-10 sm:text-sm`}
                placeholder="Full Name"
              />
              {errors.name && (
                <p className="mt-1 text-red-500 text-xs">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className={`appearance-none relative block w-full px-3 py-2 border 
                  ${errors.email ? 'border-red-500' : 'border-gray-300'}
                  placeholder-gray-500 text-gray-900 rounded-md focus:outline-none 
                  focus:ring-brand-500 focus:border-brand-500 focus:z-10 sm:text-sm`}
                placeholder="Email address"
              />
              {errors.email && (
                <p className="mt-1 text-red-500 text-xs">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                type="password"
                {...register('password')}
                className={`appearance-none relative block w-full px-3 py-2 border 
                  ${errors.password ? 'border-red-500' : 'border-gray-300'}
                  placeholder-gray-500 text-gray-900 rounded-md focus:outline-none 
                  focus:ring-brand-500 focus:border-brand-500 focus:z-10 sm:text-sm`}
                placeholder="Password"
              />
              {errors.password && (
                <p className="mt-1 text-red-500 text-xs">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                {...register('confirmPassword')}
                className={`appearance-none relative block w-full px-3 py-2 border 
                  ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}
                  placeholder-gray-500 text-gray-900 rounded-md focus:outline-none 
                  focus:ring-brand-500 focus:border-brand-500 focus:z-10 sm:text-sm`}
                placeholder="Confirm Password"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-red-500 text-xs">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          {/* Root level error */}
          {errors.root && (
            <div className="text-center text-red-500">
              {errors.root.message}
            </div>
          )}

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 
                border border-transparent text-sm font-medium rounded-md 
                text-white bg-brand-600 hover:bg-brand-700 focus:outline-none 
                focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            >
              Create Account
            </button>
          </div>
        </form>

        <div className="text-center">
          <p className="mt-2 text-sm text-gray-600">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="font-medium text-brand-600 hover:text-brand-500"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;